/**
 * Creative Analysis / Product Analysis types
 * Mirrors the Go backend creative_analysis.go response structures
 */

export interface PerformanceKPI {
  metric: string
  value: number
  formatted: string
  target: number
  wow_change: number
  status: 'good' | 'warning' | 'bad'
}

export interface CreativeReport {
  header: string
  body: string
  action_items: string
  score: number
}

export interface PerformanceChartPoint {
  date: string
  spend: number
  revenue: number
  roas: number
  cvr: number
  ctr: number
  cpc: number
  cpm: number
  cpa: number
}

export interface PerformanceReportResponse {
  kpis: PerformanceKPI[]
  report: CreativeReport
  chart_data: PerformanceChartPoint[]
  date_range: { start_date: string; end_date: string }
  generated_at: string
  from_cache: boolean
}

export interface FatigueClassification {
  ad_id: string
  ad_name: string
  campaign_name: string
  status: string
  spend: number
  revenue: number
  roas: number
  ctr: number
  cpm: number
  lifetime_days: number
  fatigue_score: number
  fatigue_label: 'Healthy' | 'Moderate' | 'Fatigued' | 'Critical'
  budget_pressure_score: number
  analysis: string
  percent_of_spend: number
}

export interface FatigueReportResponse {
  overall_score: number
  overall_label: string
  summary: string
  ads: FatigueClassification[]
  generated_at: string
  from_cache: boolean
}

// Creatives Insight types
export interface InsightAdPerformer {
  campaign_id: string
  campaign_name: string
  spend: number
  revenue: number
  roas: number
  ctr: number
  cpm: number
  conversions: number
  health_score: number
  health_label: string
}

export interface CreativeInsightResponse {
  overall_health_score: number
  overall_health_label: string
  report: CreativeReport
  top_performers: InsightAdPerformer[]
  bottom_performers: InsightAdPerformer[]
  total_ads: number
  generated_at: string
  from_cache: boolean
}

// Ad Opportunities types
export interface OpportunityAd {
  campaign_id: string
  campaign_name: string
  spend: number
  revenue: number
  roas: number
  ctr: number
  cpm: number
  conversions: number
  opportunity_type: 'scale' | 'improve' | 'test' | 'pause'
}

export interface AdOpportunitiesResponse {
  report: CreativeReport
  ads: OpportunityAd[]
  total_ads: number
  generated_at: string
  from_cache: boolean
}

// Next Steps types
export interface NextStepsResponse {
  body: string
  generated_at: string
  from_cache: boolean
}

// Analysis screen configuration
export type AnalysisScreenId = 'performance' | 'fatigue' | 'insight' | 'opportunities' | 'next-steps'

export interface AnalysisScreen {
  id: AnalysisScreenId
  title: string
  icon: string
  isActive: boolean
}

export const ANALYSIS_SCREENS: AnalysisScreen[] = [
  {
    id: 'performance',
    title: 'Performance One Pager',
    icon: 'TrendingUp',
    isActive: true,
  },
  {
    id: 'insight',
    title: 'Creatives Insight',
    icon: 'Lightbulb',
    isActive: true,
  },
  {
    id: 'fatigue',
    title: 'Fatigue',
    icon: 'Battery',
    isActive: true,
  },
  {
    id: 'opportunities',
    title: 'Ad Opportunities',
    icon: 'Target',
    isActive: true,
  },
  {
    id: 'next-steps',
    title: 'Next Steps',
    icon: 'Wand2',
    isActive: true,
  },
]
